import React, { useState, useEffect } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Divider, LinearProgress, Switch, FormControlLabel } from '@mui/material';
import { Typography } from 'components/index';
import { ExpandMore as ExpandMoreIcon, Add as AddIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import { NoteDTO } from 'common/utils/util.type';
import Tooltip from '@mui/material/Tooltip';
import { usePrompt } from '../../AppPrompt';
import { handleError } from 'common/utils/util.errors';
import WhiteFab from '../../common/WhiteFab';
import { ReportStatusEnum } from '../../report/report.type';
import { DisplayNote } from './DisplayNote';
import { AddNote } from './AddNote';
import EditNoteIcon from '@mui/icons-material/EditNote';
interface NotesProps {
  entityId: string;
  notes: NoteDTO[];
  setNotes: (notes: NoteDTO[]) => void;
  isLocked?: boolean;
  reportStatus?: string;
  apiCalls: {
    getNotes: (entityId: string) => Promise<NoteDTO[]>;
    addOrUpdateNote: (entityId: string, note: NoteDTO) => Promise<NoteDTO>;
    deleteNote: (entityId: string, noteId: string) => Promise<void>;
  };
  tooltips?: { unlockToEdit: string; reportStatus: string };
  shouldRefetch?: boolean;
  onAutogenerateNotes?: () => Promise<void>;
  isAutoGenerating?: boolean;
}

export const NotesAccordion: React.FC<NotesProps> = ({
  entityId,
  notes,
  setNotes,
  isLocked,
  reportStatus,
  apiCalls: { getNotes, addOrUpdateNote, deleteNote },
  tooltips,
  shouldRefetch,
  onAutogenerateNotes,
  isAutoGenerating,
}) => {
  const [newNote, setNewNote] = useState('');

  const [notesUpdated, setNotesUpdated] = useState(false);
  const [selectedNoteForDeletion, setSelectedNoteForDeletion] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null | undefined>(null);
  const [editedContent, setEditedContent] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(true);
  const [fetchingNotes, setFetchingNotes] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [updatingNote, setUpdatingNote] = useState(false);
  const [deletingNote, setDeletingNote] = useState(false);
  const [viewInspectorNotes, setViewInspectorNotes] = useState(false);
  const { getConfirmation } = usePrompt();

  const sortNotesByDate = (notes: NoteDTO[]) => {
    return [...notes].sort((a, b) => new Date(b.createdOn || '').getTime() - new Date(a.createdOn || '').getTime());
  };

  // Keep notes sorted by date
  useEffect(() => {
    if (notes.length > 0) {
      const sortedNotes = sortNotesByDate(notes);
      // Only update if the order has changed
      if (JSON.stringify(sortedNotes) !== JSON.stringify(notes)) {
        setNotes(sortedNotes);
      }
    }
  }, [notes]);

  useEffect(() => {
    if (isLocked) {
      setEditingNoteId(null);
      setEditedContent('');
      setNewNote('');
      setShowAddNote(false);
    }
  }, [isLocked]);

  useEffect(() => {
    const fetchNotes = async () => {
      setFetchingNotes(true);
      try {
        const fetchedNotes = await getNotes(entityId);
        if (Array.isArray(fetchedNotes)) {
          setNotes(sortNotesByDate(fetchedNotes));
        }
        setNotesUpdated(false);
      } catch (error) {
        console.error('Error fetching notes:', error);
        toast.error('Failed to fetch notes');
      } finally {
        setFetchingNotes(false);
      }
    };
    fetchNotes();
  }, [entityId, notesUpdated, shouldRefetch]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const addedNote = await addOrUpdateNote(entityId, { content: newNote });
      if (addedNote) {
        setNotesUpdated(true);
        setShowAddNote(false);
        toast.success('Note Added Successfully!');
      }
    } catch (error) {
      handleError(error);
    } finally {
      setAddingNote(false);
    }
    setNewNote('');
  };

  const handleDeleteNote = async (noteId: string) => {
    if ((notes.find(n => n.id === noteId) as any)?.isPayappNote) {
      toast.error('PayApp notes cannot be deleted.');
      return;
    }
    if (editingNoteId) {
      toast.error('A note is currently being edited. Please finish editing before modifying another note.');
      return;
    }
    const isConfirmed = await getConfirmation({
      description: `Are you sure you want to delete this note?`,
      confirmButton: {
        label: 'Delete',
        color: 'error',
      },
      cancelButton: {
        label: 'Cancel',
        color: 'secondary',
      },
    });
    if (!isConfirmed) return;
    setDeletingNote(true);
    setSelectedNoteForDeletion(noteId);
    try {
      await deleteNote(entityId, noteId);
      setNotesUpdated(true);
      toast.success('Note deleted successfully');
    } catch (error) {
      handleError(error);
    } finally {
      setDeletingNote(false);
      setSelectedNoteForDeletion(null);
    }
  };

  const handleEditClick = (note: NoteDTO) => {
    if ((note as any).isPayappNote) {
      toast.error('PayApp notes cannot be edited.');
      return;
    }
    if (!editingNoteId) {
      setEditingNoteId(note.id);
      setEditedContent(note.content);
    } else {
      toast.error('A note is currently being edited. Please finish editing before modifying another note.');
    }
  };
  const handleUpdateNote = async (noteId: any) => {
    if (!editedContent.trim()) return;
    setUpdatingNote(true);
    try {
      await addOrUpdateNote(entityId, { id: noteId, content: editedContent });
      setNotesUpdated(true);
      setEditingNoteId(null);
      toast.success('Note updated successfully');
    } catch (error) {
      handleError(error);
    } finally {
      setUpdatingNote(false);
    }
  };
  const handleToggleAddNote = (event: any) => {
    if (isAccordionExpanded) {
      event.stopPropagation();
    }
    if (!editingNoteId) {
      setShowAddNote(true);

      setIsAccordionExpanded(true);
    } else {
      toast.error('A note is currently being edited. Please finish editing before adding another note.');
    }
  };

  const handleCancelAddNote = () => {
    setShowAddNote(false);
    setNewNote('');
  };

  const filteredNotes = viewInspectorNotes ? notes.filter(note => (note as any).isPayappNote) : notes;
  return (
    <Accordion
      expanded={isAccordionExpanded}
      onChange={() => setIsAccordionExpanded(!isAccordionExpanded)}
      elevation={3}
      style={{ marginTop: '5px', borderRadius: '8px' }}
      sx={{
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
        <Typography variant='h5' color={'primary'} style={{ display: 'flex', alignItems: 'center' }}>
          <CommentOutlinedIcon color='primary' style={{ marginRight: '8px' }} />
          Notes ({filteredNotes.length})
        </Typography>
        <FormControlLabel
          onClick={event => event.stopPropagation()}
          control={<Switch checked={viewInspectorNotes} onChange={e => setViewInspectorNotes(e.target.checked)} />}
          label='View Inspector Notes'
          sx={{ ml: 'auto', '& .MuiTypography-root': { fontSize: '0.9rem' } }}
        />
        {onAutogenerateNotes && (
          <Tooltip title={isLocked ? tooltips?.unlockToEdit : 'Autogenerate notes'} arrow={false}>
            <span style={{ display: 'inline-flex', marginRight: '8px' }}>
              <WhiteFab
                aria-label='autogenerate'
                icon={<EditNoteIcon fontSize='small' />}
                textColor='primary'
                onClick={e => {
                  e.stopPropagation();
                  onAutogenerateNotes();
                }}
                size='small'
                disabled={isLocked || isAutoGenerating}
              />
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={
            isLocked && reportStatus === ReportStatusEnum.Completed
              ? tooltips?.reportStatus
              : isLocked
                ? tooltips?.unlockToEdit
                : 'Add note'
          }
          arrow={false}
        >
          <span style={{ display: 'inline-flex', marginRight: '1px' }}>
            <WhiteFab
              aria-label='add'
              icon={<AddIcon fontSize='small' />}
              textColor='primary'
              onClick={handleToggleAddNote}
              size='small'
              disabled={isLocked || addingNote || updatingNote || deletingNote}
            />
          </span>
        </Tooltip>
      </AccordionSummary>
      <Divider />
      <AccordionDetails
        style={{
          flexDirection: 'column',
          maxHeight: '300px',
          overflowY: 'auto',
        }}
      >
        {showAddNote && (
          <AddNote
            newNote={newNote}
            setNewNote={setNewNote}
            handleAddNote={handleAddNote}
            handleCancelAddNote={handleCancelAddNote}
            isAdding={addingNote}
            isLocked={isLocked}
          />
        )}
        {fetchingNotes ? <LinearProgress /> : null}
        {filteredNotes.map((note, index) => (
          <DisplayNote
            key={note.id}
            note={note}
            index={index}
            editingNoteId={editingNoteId}
            isLocked={isLocked}
            tooltips={tooltips}
            setEditingNoteId={setEditingNoteId}
            handleEditClick={handleEditClick}
            handleDeleteNote={handleDeleteNote}
            handleUpdateNote={handleUpdateNote}
            editedContent={editedContent}
            setEditedContent={setEditedContent}
            updatingNote={updatingNote}
            deletingNote={deletingNote}
            reportStatus={reportStatus}
            selectedNoteForDeletion={selectedNoteForDeletion}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};
